import { Box, Checkbox, FormControlLabel, Grid, Theme } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PoolTypography } from '@synergy/components';
import { UserRole } from '@synergy/models';
import { createStyles, makeStyles } from '@mui/styles';
import { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';

import { ContactDetailPermissionsRoleField } from './contact-detail-permissions-role-field';
import { ContactPermissionsFormValues } from '../models/contact-permissions-form-values';
import { contactShopAdminMembersSelector } from '../store/contact-detail-permissions-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        grid: {
            marginTop: 0,
        },
    })
);

/**
 * Shop admin is granted per member (#2109): a person in several members is
 * admin only of the ones ticked here. Newly made shop admin, they are admin of
 * all of them until narrowed.
 */
const ShopAdminMembers = () => {
    const { values, setFieldValue } = useFormikContext<ContactPermissionsFormValues>();
    const members = useAppSelector(contactShopAdminMembersSelector);
    const isShopAdmin = !!values.roles[UserRole.ShopAdmin];
    const wasShopAdmin = useRef(isShopAdmin);

    useEffect(() => {
        if (isShopAdmin && !wasShopAdmin.current) {
            setFieldValue(
                'shopAdminAccountIds',
                members.map(member => member.id)
            );
        }
        wasShopAdmin.current = isShopAdmin;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShopAdmin]);

    if (!isShopAdmin || members.length < 2) {
        return null;
    }

    const selected = values.shopAdminAccountIds ?? [];

    return (
        <Box sx={{ mt: 1 }}>
            <PoolTypography styleType="sectionTitle" id="app.contactsAndOrganizations.contact.portal.shopAdminOf" />
            {members.map(member => (
                <FormControlLabel
                    key={member.id}
                    sx={{ display: 'flex' }}
                    control={
                        <Checkbox
                            size="small"
                            checked={selected.includes(member.id)}
                            onChange={(_, checked) =>
                                setFieldValue(
                                    'shopAdminAccountIds',
                                    checked ? [...selected, member.id] : selected.filter(id => id !== member.id)
                                )
                            }
                        />
                    }
                    label={member.name}
                />
            ))}
            <Box sx={{ fontSize: '12px', color: 'text.secondary' }}>
                <FormattedMessage id="app.contactsAndOrganizations.contact.portal.shopAdminOfHint" />
            </Box>
        </Box>
    );
};

export const ContactDetailPermissionsRolesCustomer = () => {
    const classes = useStyles();

    return (
        <>
            <PoolTypography styleType="sectionTitle" id="app.contactsAndOrganizations.contact.portal.customer" />

            <Grid container={true} className={classes.grid} columnSpacing={2}>
                <Grid item={true} xs={6}>
                    <ContactDetailPermissionsRoleField role={UserRole.ShopUser} />
                </Grid>
                <Grid item={true} xs={6}>
                    <ContactDetailPermissionsRoleField role={UserRole.ShopAdmin} />
                    <ShopAdminMembers />
                </Grid>
            </Grid>
        </>
    );
};
